/** Ficha de la persona → Accesos (T5): qué piden las políticas vs qué tiene; el roster registra lo que pasó. */
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Lock } from 'lucide-react';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { InternalTable, EmptyState, LoaderBlock } from '@/components/internal/shared';
import { EnumBadge, type BadgeTone } from '@/components/ui/enum-badge';
import { toastError } from '@/lib/toast-error';
import { useOrgCatalogMutations, useOrgClientsAll, useOrgPersonAccess } from '@/hooks/useOrg';
import { OrgClientLink } from '@/components/org/org-ui';
import { ORG_ACCESS_STATUSES, type OrgAccessStatus } from '../../../../shared/types/org';
import type { RouterOutputs } from '@/lib/api-types';

type Item = RouterOutputs['org']['access']['forPerson']['items'][number];
const TONE: Record<Item['state'], BadgeTone> = { missing: 'danger', requested: 'warning', granted: 'success', extra: 'muted' };
const NONE = '__none__';

export function OrgAccessSection({ email, employeeId, canRecord }: { email: string; employeeId: number; canRecord: boolean }) {
  const { t } = useTranslation();
  const q = useOrgPersonAccess(email);
  const clients = useOrgClientsAll();
  const clientName = (id: string) => clients.data?.find((c) => c.client_id === id)?.display_name ?? id;
  const [recording, setRecording] = useState<Item | null>(null);
  if (q.isLoading) return <LoaderBlock />;
  if (q.isError) {
    if (q.error.data?.code === 'FORBIDDEN') {
      return (
        <Alert>
          <Lock className="h-4 w-4" />
          <AlertTitle>{t('org.access.hidden_title', 'Solo lo ven su líder y el roster')}</AlertTitle>
          <AlertDescription>{t('org.access.hidden_body', 'Los accesos de una persona los ven ella misma, su líder directo y quienes están en los permisos del Org Service.')}</AlertDescription>
        </Alert>
      );
    }
    return <EmptyState title={t('org.access.error', 'No se pudieron calcular los accesos.')} />;
  }
  const { items, summary } = q.data!;
  const cols = [
    { key: 'state', label: t('org.access.col_state', 'Estado'), render: (i: Item) => <EnumBadge value={i.state} tone={TONE[i.state]} label={t(`org.access.state_${i.state}`, i.state)} /> },
    { key: 'tool', label: t('org.access.col_tool', 'Herramienta'), render: (i: Item) => <span className="font-medium">{i.toolName}{i.toolRole ? <span className="ml-1 text-xs text-muted-foreground">({i.toolRole})</span> : null}</span> },
    { key: 'client', label: t('org.access.col_client', 'Cliente'), render: (i: Item) => (i.clientId ? <OrgClientLink clientId={i.clientId} name={clientName(i.clientId)} /> : '—') },
    { key: 'moment', label: t('org.access.col_moment', 'Cuándo'), render: (i: Item) => (i.moment ? t(`org.policies.moment_${i.moment}`, i.moment) : '—') },
    { key: 'approver', label: t('org.access.col_approver', 'Aprueba'), render: (i: Item) => <span className="text-muted-foreground">{i.approverEmail ?? '—'}</span> },
    { key: 'evidence', label: t('org.access.col_evidence', 'Evidencia'), render: (i: Item) => <span className="text-xs text-muted-foreground">{i.evidenceSystem ? `${t(`org.access.system_${i.evidenceSystem}`, i.evidenceSystem)}${i.evidenceRef ? ` · ${i.evidenceRef}` : ''}` : '—'}</span> },
    ...(canRecord ? [{ key: 'actions', label: '', render: (i: Item) => <Button size="sm" variant="outline" onClick={() => setRecording(i)}>{t('org.access.record', 'Registrar')}</Button> }] : []),
  ];
  return (
    <div className="space-y-2">
      <div className="flex flex-wrap gap-2 text-sm">
        {(['missing', 'requested', 'granted', 'extra'] as const).map((k) => <EnumBadge key={k} value={k} tone={TONE[k]} label={`${t(`org.access.state_${k}`, k)}: ${summary[k]}`} />)}
      </div>
      {items.length ? <InternalTable columns={cols} rows={items} rowKey={(i) => `${i.toolSlug}@${i.clientId ?? ''}`} /> : <EmptyState title={t('org.access.empty', 'Ninguna política le pide accesos todavía y no tiene accesos registrados.')} />}
      {recording && <RecordDialog employeeId={employeeId} item={recording} onClose={() => setRecording(null)} />}
    </div>
  );
}

function RecordDialog({ employeeId, item, onClose }: { employeeId: number; item: Item; onClose: () => void }) {
  const { t } = useTranslation();
  const m = useOrgCatalogMutations();
  const [status, setStatus] = useState<OrgAccessStatus>(item.state === 'granted' || item.state === 'extra' ? 'revoked' : 'granted');
  const [system, setSystem] = useState<string>(item.evidenceSystem && item.evidenceSystem !== 'vault' ? item.evidenceSystem : NONE);
  const [ref, setRef] = useState(item.evidenceRef ?? '');
  const save = () => {
    m.accessRecord.mutate(
      { employeeId, toolSlug: item.toolSlug, clientId: item.clientId, status, evidenceSystem: system === NONE ? null : (system as 'cognito' | 'google-group' | 'external'), evidenceRef: system === NONE ? null : ref || null },
      { onSuccess: () => { toast.success(t('org.access.recorded', 'Acceso registrado')); onClose(); }, onError: (err) => toastError(err, 'org.access.error') },
    );
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t('org.access.record_title', 'Registrar lo que pasó con {{tool}}', { tool: item.toolName })}</DialogTitle>
          <DialogDescription>{t('org.access.record_body', 'Esto es el registro, no el alta: el acceso se da en la herramienta o en el Vault; acá queda la evidencia.')}</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1">
            <Label htmlFor="acc-status">{t('org.access.col_state', 'Estado')}</Label>
            <Select value={status} onValueChange={(v) => setStatus(v as OrgAccessStatus)}>
              <SelectTrigger id="acc-status"><SelectValue /></SelectTrigger>
              <SelectContent>{ORG_ACCESS_STATUSES.map((s) => <SelectItem key={s} value={s}>{t(`org.access.status_${s}`, s)}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <Label htmlFor="acc-system">{t('org.access.evidence_system', 'Sistema que lo administra')}</Label>
            <Select value={system} onValueChange={setSystem}>
              <SelectTrigger id="acc-system"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value={NONE}>—</SelectItem>
                {['cognito', 'google-group', 'external'].map((s) => <SelectItem key={s} value={s}>{t(`org.access.system_${s}`, s)}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <Label htmlFor="acc-ref">{t('org.access.evidence_ref', 'Referencia (id de credencial, grupo, ticket…)')}</Label>
            <Input id="acc-ref" value={ref} onChange={(e) => setRef(e.target.value)} disabled={system === NONE} />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>{t('common.cancel', 'Cancelar')}</Button>
          <Button onClick={save} disabled={m.busy}>{t('common.save', 'Guardar')}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
